<template>

  <v-card v-if="location && order" class="route-builder-order-location-card">
    <v-container fluid>
      <v-row>
        <!-- Index and Location Information -->
        <v-col sm="5" align="left">
          <div class="mb-5">
            <div class="arrival-index">{{ locationIndexForDisplay }}</div>
            <div class="order-number">Order: {{ order.order_number }}</div>
          </div>

          <v-chip label outlined color="green accent-4" v-if="isPickup">
            PICKUP
          </v-chip>
          <v-chip label outlined color="light-blue accent-3" v-else>
            DROPOFF
          </v-chip>

          <div class="pt-4 ml-2 loc-info">
            <v-icon size="15" color="white">mdi-map-marker-outline</v-icon>
            <span class="ml-2 loc-label">
              <a @click="goToLocationPage">{{ locationName }}</a>
            </span>
          </div>

          <div class="pt-2 ml-2 loc-info" v-if="brandName">
            <span class="ml-6">{{ brandName }}</span>
          </div>
        </v-col>

        <!-- Receiving Times / Requires Appt / Notes -->
        <v-col sm="3" class="receiving-times" align="left">
          <template v-if="location.requires_appointment">
            <div class="mt-2 requires-appt-badge">
              Requires Appt
            </div>
            <div class="mt-3 ml-2" v-if="location.appointment_url">
              <a :href="formatUrl(location.appointment_url)" :title="location.appointment_url" target="_new">appt website</a>
              <v-icon class="ml-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
            </div>
            <v-checkbox
                class="checkbox-appt-confirm"
                label="Confirmed?"
                v-model="order.appointment_confirmed"
                @click="emitDirty('appointment_confirmed')" />
          </template>
          <template v-else>
            <div class="mb-2 label">
              Receiving Times
            </div>
            <div class="receiving-times-text">
              {{ receivingTimesAsText }}
            </div>
          </template>
          <div class="mt-1 d-flex align-center" v-if="location.notes">
            <span style="font-size:15px;">Appointment notes</span>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" style="font-size:20px;cursor:pointer;" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>{{ location.notes }}</div>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Expected Delivery & Estimated Arrival times -->
        <v-col sm="3" class="delivery-times" align="left">
          <div class="label" style="margin-bottom:6px;">
            Expected By
          </div>
          <div class="expected-date">
            {{ expectedDateForDisplay }}
          </div>
          <div style="margin-top:16px;margin-bottom:6px;" class="label">
            Estimated Arrival
          </div>
          <div class="arrival-time">
            {{ arrivalTimeForDisplay }}
            <!-- arrival time tooltip (about wait times) -->
            <v-tooltip bottom v-if="order.estimated_receiver_arrival_time">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="icon-arrival-time-info"
                  v-bind="attrs"
                  v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>Considers:</div>
              <div>Pickup wait time: {{ pickupWaitTime }} mins</div>
              <div>Dropoff wait time: {{ dropoffWaitTime }} mins</div>
            </v-tooltip>
          </div>
        </v-col>

        <!-- Remove order -->
        <v-col sm="1" class="d-flex flex-column align-center justify-center remove-order" align="left" v-if="showRemove">
          <dialog-confirm-button
              message="Are you sure you want to remove this order from the Route?"
              buttonStyle="plain"
              icon="mdi-delete"
              iconColor="white"
              v-on:confirmed="emitRemoveOrder" />
        </v-col>
      </v-row>
    </v-container>
  </v-card>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'RouteBuilderOrderLocationCard',

  components: {
    DialogConfirmButton
  },

  props: {
    location: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    locationIndex: {
      type: Number,
      default: 0
    },
    showRemove: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    activeOrg () {
      return this.$store.getters['auth/activeOrg']
    },
    pickupWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderPickupWaitTime(this.activeOrg.id) : ''
    },
    dropoffWaitTime () {
      return (this.activeOrg) ? this.$root.getOrderDropoffWaitTime(this.activeOrg.id) : ''
    },
    isPickup () {
      return this.location.model === 'Shipper'
    },
    locationIndexForDisplay () {
      return this.locationIndex
    },
    locationName () {
      return this.location.name
    },
    brandName () {
      return this.order.thirdparty_brand_name
    },
    receiver () {
      return this.order.receiver || {}
    },
    receivingTimesAsText () {
      return (this.location.available_times)
        ? this.location.available_times.as_text
        : '-'
    },
    expectedDateForDisplay () {
      return (this.order.expected_delivery)
        ? this.$root.utils.dateFormat(this.order.expected_delivery, 'MMM D, YYYY')
        : '-'
    },
    arrivalTimeForDisplay () {
      const arrivalTime = (this.isPickup) ? this.order.estimated_shipper_arrival_time : this.order.estimated_receiver_arrival_time
      return (arrivalTime)
        ? this.$root.utils.dateFormat(arrivalTime, 'h:mm a')
        : '-'
    }
  },

  methods: {
    // Ensures url has protocol in front
    formatUrl (url) {
      return (url.startsWith('http://') || url.startsWith('https://'))
        ? url
        : `http://${url}`
    },

    // Emit order removed
    emitRemoveOrder () {
      this.$emit('removeOrder', this.order)
    },

    // Emit change occurred (with property name and order)
    emitDirty (property) {
      this.$emit('dirty', property, this.order)
    },

    // Go to the location page
    goToLocationPage () {
      if (this.isPickup) this.$root.goTo('shipper-item', { shipperId: this.location.id })
      else this.$root.goTo('receiver-item', { receiverId: this.location.id })
    }
  }
}

</script>

<style>
  .route-builder-order-location-card .checkbox-appt-confirm .v-label {
    font-size: 15px !important;
  }

  .requires-appt-badge {
    font-size: 0.95em;
    padding: 0 6px;
    text-align: center;
    border-radius: 3px;
    color: #ffffff;
    background-color: #f94851;
  }

  /* ---------------------------------------------------------- arrival-index */

  .route-builder-order-location-card .arrival-index {
    display: inline;
    width: 27px;
    color: #000000;
    background-color: #e2e2e2;
    border: 1px solid #ffffff;
    padding: 2px 10px 2px 10px;
    margin-right: 12px;
  }

  /* ------------------------------------------------------------- title-area */

  .route-builder-order-location-card .label {
    color: #9c9c9c;
    font-size: 14px;
  }

  .route-builder-order-location-card .order-number {
    display: inline;
    font-size: 22px !important;
  }

  /* --------------------------------------------------------------- loc-info */

  .route-builder-order-location-card .loc-info {
    color: #9c9c9c;
    font-size: 14px;
  }

  .route-builder-order-location-card .label-delivering-to {
    color: #525252;
    font-size: 13px;
    text-align: center;
    width: 100px;
    /* font-style: italic; */
  }

  /* -------------------------------------------------------- receiving-times */

  .receiving-times .receiving-times-text {
    font-size: 14px;
    margin-top: 1px;
    margin-bottom: 4px;
  }

  /* -------------------------------------------------------- delivery-times */

  .delivery-times .expected-date {
    font-size: 16px;
  }

  .delivery-times .arrival-time {
    font-size: 22px;
  }

  .icon-arrival-time-info {
    cursor: pointer;
    color: #9c9c9c !important;
    font-size: 17px !important;
    position: absolute !important;
    margin-left: 8px;
  }

  .icon-arrival-time-info:hover {
    color: #ffffff !important;
  }

  /* -------------------------------------------------------- remove-order */

  .icon-remove-order {
    font-size: 22px !important;
  }
</style>
